import { Execute, ReservoirClient, createClient } from "@reservoir0x/reservoir-sdk";
import { Web3Config } from "../config/Web3Config";
import { WalletClient } from "viem";
import { ILogger } from "../../cityversetycoon/logger/ILogger";
import { Account } from "thirdweb/wallets";
import { viemAdapter } from "thirdweb/adapters/viem";
import { ThirdwebClient } from "thirdweb";
import { ThirdWebAnalytic } from "./ThirdWebAnalytic";

type ListInResult = {
    operationResult: boolean,
    stepsResult: Array<Execute["steps"]>,
    orderId: string
} | UnauthResult;

type UnauthResult = "unauthorized";

export class ReservoirSDK {
    private static readonly UNAUTH_MESSAGE = "Missing auth token when validating user identity";
    private static readonly UNAUTH_TOKEN_ERROR_MESSAGE = "auth token is missing";
    private static readonly EXPIRATION_TIME = 315600000/*10 years in sec*/;

    private readonly reservoirClient: ReservoirClient;

    public constructor(
        private readonly web3Config: Web3Config, 
        private readonly thirdwebClient: ThirdwebClient, 
        private readonly thirdWebAnalytic: ThirdWebAnalytic, 
        private readonly logger: ILogger
    ) {
        this.reservoirClient = createClient({
            chains: [this.web3Config.reservoirTargetChain],
            apiKey: this.web3Config.reservoirApiKey,
            logLevel: 4,
            source: this.web3Config.reservoirSourceName,
        });
    }

    public async listIn(provider: Account, tokenAddress: string, tokenId: BigInt, price: string): Promise<ListInResult> {
        try {
            const viemWallet: WalletClient = viemAdapter.walletClient.toViem({
                client: this.thirdwebClient,
                chain: this.web3Config.targetChain,
                account: provider as Account,
            });

            const expirationTime = Math.floor(new Date().getTime() / 1000) + ReservoirSDK.EXPIRATION_TIME;

            const stepsResult: Array<Execute["steps"]> = [];

            const listTokenResult = await this.reservoirClient.actions.listToken({
                listings: [
                    {
                        token: `${tokenAddress}:${tokenId.toString()}`,
                        weiPrice: price,
                        currency: this.web3Config.reservoirDevUsdcCurrency,
                        orderbook: "reservoir",
                        orderKind: "seaport-v1.5",
                        expirationTime: expirationTime.toString(),
                        automatedRoyalties: true
                    }
                ],
                chainId: this.web3Config.targetChain.id,
                wallet: viemWallet,
                onProgress: (step: Execute["steps"]) => {
                    this.logger.debug(`[ReservoirSDK] List in steps: \n${JSON.stringify(step)}`);
                    stepsResult.push(step);
                },
            });

            const orderIds = stepsResult.flatMap(
                (step) => step?.flatMap(
                    (stepItem) => stepItem?.items?.flatMap(
                        item => item?.orderData?.flatMap(
                            order => order?.orderId))));
            const orderId = orderIds?.find((orderId) => orderId != null);

            if (typeof listTokenResult === "boolean") {
                return {
                    operationResult: listTokenResult,
                    stepsResult,
                    orderId
                }
            }
            else {
                return {
                    operationResult: !listTokenResult.some((step) => step.error != null),
                    stepsResult,
                    orderId
                }
            }
        }
        catch (e: unknown) {
            const errorStr = String(e);
            this.logger.debug(`[ReservoirSDK] Exception on list in operation: ${e}`);
            if (e instanceof Error && e.message?.includes(ReservoirSDK.UNAUTH_MESSAGE)
                || errorStr?.includes(ReservoirSDK.UNAUTH_TOKEN_ERROR_MESSAGE)) {
                this.thirdWebAnalytic.trackMissingAuthTokenEvent();
                return "unauthorized";
            }
            return null;
        }
    }

    public async remove(provider: Account, orderId: string): Promise<boolean | UnauthResult> {
        try {
            const viemWallet: WalletClient = viemAdapter.walletClient.toViem({
                client: this.thirdwebClient,
                chain: this.web3Config.targetChain,
                account: provider as Account,
            });

            const result = await this.reservoirClient.actions.cancelOrder({
                ids: [orderId],
                chainId: this.web3Config.targetChain.id,
                wallet: viemWallet,
                onProgress: (step: Execute["steps"]) => {
                    this.logger.debug(`[ReservoirSDK] List in steps: \n${JSON.stringify(step)}`);
                },
            });
            return result;
        }
        catch (e: unknown) {
            this.logger.debug(`[ReservoirSDK] Exception on remove in operation: ${e}`);
            if (e instanceof Error && e.message?.includes(ReservoirSDK.UNAUTH_MESSAGE)) {
                this.thirdWebAnalytic.trackMissingAuthTokenEvent();
                return "unauthorized";
            }
            return false;
        }
    }

    public async buyNft(provider: Account,  orderId: string) {
        try {
            const viemWallet: WalletClient = viemAdapter.walletClient.toViem({
                client: this.thirdwebClient,
                chain: this.web3Config.targetChain,
                account: provider as Account,
            });

            let stepsResult: Execute["steps"] = [];
            let isTradeCompleted = false;

            const tradeResult = await this.reservoirClient.actions.buyToken({
                items: [
                    {
                        quantity: 1,
                        orderId: orderId,
                        fillType: "trade",
                    },
                ],
                chainId: this.web3Config.targetChain.id,
                wallet: viemWallet,
                onProgress: async (steps: Execute["steps"]) => {
                    stepsResult = steps;

                    if (steps &&
                        steps[1] &&
                        steps[1].items &&
                        steps[1].items[0].status === "complete") {
                        isTradeCompleted = true;
                    }
                },
            });
            return {
                operationResult: tradeResult,
                stepsResult,
                isTradeCompleted,
            };
        } catch (e: unknown) {
            this.logger.debug(`[ReservoirSDK] Exception on buy token in operation: ${e}`);
            if (e instanceof Error && e.message?.includes(ReservoirSDK.UNAUTH_MESSAGE)) {
                this.thirdWebAnalytic.trackMissingAuthTokenEvent();
                return "unauthorized";
            }
            return null;
        }
    }

    static usdToEth(value: number) {
        return ReservoirSDK.round(value / 3000, 8) * 10 ** 18;
    }

    static toUsdc(value: number) {
        return value * 10 ** 6;
    }

    static round(x: number, accuracy = 2): number {
        return Math.round(x * Math.pow(10, accuracy)) / Math.pow(10, accuracy);
    }
}
